import { z } from 'zod';
import { emailSchema, localeSchema, uuidSchema } from './common.js';
import { tenantRoleSchema } from './tenant.js';
/**
 * Login-/2FA-Schemas. Better-Auth bringt eigene Request-Validierung mit —
 * diese Schemas gelten fuer das Admin-UI-Formular und die Tests, damit die
 * Regeln an genau einer Stelle stehen (Invariante 2).
 */
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Passwort erforderlich.'),
});
/** TOTP-Code: 6 Ziffern; Backup-Codes werden separat eingeloest. */
export const totpCodeSchema = z.object({
    code: z.string().regex(/^\d{6}$/, 'Sechsstelliger Code erforderlich.'),
    trust_device: z.boolean().default(false),
});
/**
 * Passwort-Policy — dieselben Grenzen wie `minPasswordLength` /
 * `maxPasswordLength` in `api/src/auth/auth.ts`. Als Konstanten exportiert,
 * damit das UI die Zahl in seine uebersetzten Meldungen einsetzen kann,
 * statt sie ein zweites Mal hinzuschreiben.
 */
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 256;
export const passwordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`)
    .max(MAX_PASSWORD_LENGTH);
/** Mitgliedschaft, wie sie das UI nach dem Login sieht. */
export const sessionMembershipSchema = z.object({
    tenant_id: uuidSchema,
    tenant_name: z.string(),
    tenant_slug: z.string(),
    member_id: uuidSchema,
    role: tenantRoleSchema,
    /** Leer = Zugriff auf alles im Mandanten (Spec 3.2). */
    scoped: z.boolean(),
    /**
     * `tenants.settings.ai_enabled` (Spec 3.2). Steht hier, damit das UI die
     * komplette KI-Oberflaeche ohne zweite Anfrage ausblenden kann — die API
     * lehnt sie unabhaengig davon ab (Phase 4).
     */
    ai_enabled: z.boolean(),
    /**
     * `tenants.settings.csat_enabled` (Spec 3.2 / 8.6) — gleiche Ueberlegung:
     * ohne CSAT gibt es im Ticket kein Bewertungs-Panel und keinen Knopf
     * „Bewertung anfragen" (Phase 5).
     */
    csat_enabled: z.boolean(),
});
/** Antwort von `GET /api/v1/me` — was das Admin-UI zum Rendern braucht. */
export const currentUserSchema = z.object({
    id: uuidSchema,
    email: emailSchema,
    name: z.string(),
    image: z.string().nullable(),
    two_factor_enabled: z.boolean(),
    is_platform_admin: z.boolean(),
    /** Bevorzugte Oberflaechensprache (`users.locale`, Spec 14). */
    locale: localeSchema,
    memberships: z.array(sessionMembershipSchema),
});
/**
 * Eigenverwaltung des Kontos (`PATCH /api/v1/me`).
 *
 * Nur `name` und `locale` — die E-Mail-Adresse laeuft ueber den
 * Verifizierungsweg von Better-Auth (`POST /api/auth/change-email`) und
 * **nie** ueber diese Route.
 */
export const profileNameSchema = z
    .string()
    .trim()
    .min(1, 'Bitte gib einen Namen an.')
    .max(120, 'Der Name darf hoechstens 120 Zeichen haben.');
export const updateProfileSchema = z
    .object({
    name: profileNameSchema.optional(),
    locale: localeSchema.optional(),
})
    .refine((value) => value.name !== undefined || value.locale !== undefined, {
    message: 'Es wurde nichts zum Aendern uebergeben.',
});
/**
 * Formular „E-Mail-Adresse aendern". Der Aufruf geht direkt an Better-Auth;
 * das Schema steht hier, damit die Regel nicht ein zweites Mal im UI steht
 * (Invariante 2) — genau wie `loginSchema` und `totpCodeSchema`.
 */
export const changeEmailSchema = z.object({
    new_email: emailSchema,
});
//# sourceMappingURL=auth.js.map